/* Axios instance + endpoint helpers. Uses httpOnly cookies (withCredentials)
 * and also sends a Bearer token saved in localStorage as a fallback so the PWA
 * can authenticate even when cookies are unavailable. */
import axios from "axios";
import { kvSet } from "./db";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
    timeout: 15000,
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem("rom_access_token");
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

/* ---- Silent token refresh on 401 (expired/invalid access token) ----
 * Single-flight: concurrent 401s wait for one refresh, then retry. A failed
 * refresh clears the token and bounces to /login (no retry loop). */
let isRefreshing = false;
let waiters = [];
function flushWaiters(token) {
    waiters.forEach((cb) => cb(token));
    waiters = [];
}
async function persistAccessToken(token) {
    try { localStorage.setItem("rom_access_token", token); } catch {}
    try { await kvSet("rom_access_token", token); } catch {}
}

api.interceptors.response.use(
    (res) => res,
    async (error) => {
        const original = error.config || {};
        const status = error.response?.status;
        const detail = String(error.response?.data?.detail || "");
        const isExpired = status === 401 && /expired|invalid token/i.test(detail);
        const url = original.url || "";
        const skip = url.includes("/auth/refresh") || url.includes("/auth/login") || url.includes("/auth/logout");

        if (!isExpired || original._retry || skip) return Promise.reject(error);

        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                waiters.push((token) => {
                    if (!token) return reject(error);
                    original._retry = true;
                    original.headers = original.headers || {};
                    original.headers.Authorization = `Bearer ${token}`;
                    resolve(api(original));
                });
            });
        }

        original._retry = true;
        isRefreshing = true;
        try {
            const { data } = await api.post("/auth/refresh");
            const newToken = data?.access_token;
            if (newToken) await persistAccessToken(newToken);
            isRefreshing = false;
            flushWaiters(newToken || true);
            if (newToken) {
                original.headers = original.headers || {};
                original.headers.Authorization = `Bearer ${newToken}`;
            }
            return api(original);
        } catch (e) {
            isRefreshing = false;
            flushWaiters(null);
            try { localStorage.removeItem("rom_access_token"); } catch {}
            try { await kvSet("rom_access_token", null); } catch {}
            if (!window.location.pathname.includes("/login")) {
                window.location.assign("/login");
            }
            return Promise.reject(error);
        }
    }
);

export function formatApiError(err) {
    const d = err?.response?.data?.detail;
    if (!d) return err?.message || "Network error";
    if (typeof d === "string") return d;
    if (Array.isArray(d))
        return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" · ");
    return String(d);
}

export const endpoints = {
    health: () => api.get("/health", { timeout: 4000 }),
    login: (b) => api.post("/auth/login", b),
    logout: () => api.post("/auth/logout"),
    me: () => api.get("/auth/me"),
    listUsers: () => api.get("/auth/users"),
    register: (b) => api.post("/auth/register", b),
    seedDemoData: () => api.post("/admin/seed-demo"),

    rateSchedules: () => api.get("/rate-schedules"),
    activeRateSchedule: () => api.get("/rate-schedules/active"),
    vendorLeaderboard: () => api.get("/rate-schedules/vendor-leaderboard"),
    saveRateSchedule: (b) => api.post("/rate-schedules", b),
    activateRateSchedule: (id) => api.post(`/rate-schedules/${id}/activate`),
    signRateSchedule: (id, b) => api.post(`/rate-schedules/${id}/sign`, b),
    sendRateSchedule: (id, b) => api.post(`/rate-schedules/${id}/send`, b),

    publicGetDoc: (token) => api.get(`/public/documents/${token}`),
    publicSignDoc: (token, b) => api.post(`/public/documents/${token}/sign`, b),
    publicRespondDoc: (token, b) => api.post(`/public/documents/${token}/respond`, b),

    sendRomProposal: (jobId, b) => api.post(`/jobs/${jobId}/send`, b),
    romProposals: (jobId) => api.get(`/jobs/${jobId}/proposals`),

    clients: () => api.get("/clients"),
    saveClient: (b) => api.post("/clients", b),
    client: (id) => api.get(`/clients/${id}`),
    clientSummary: (id) => api.get(`/clients/${id}/summary`),
    patchClient: (id, b) => api.patch(`/clients/${id}`, b),
    addClientContact: (id, b) => api.post(`/clients/${id}/contacts`, b),
    updateClientContact: (id, cid, b) => api.put(`/clients/${id}/contacts/${cid}`, b),
    deleteClientContact: (id, cid) => api.delete(`/clients/${id}/contacts/${cid}`),
    addClientActivity: (id, b) => api.post(`/clients/${id}/activities`, b),
    addClientAppointment: (id, b) => api.post(`/clients/${id}/appointments`, b),
    updateClientAppointment: (id, aid, b) => api.put(`/clients/${id}/appointments/${aid}`, b),
    deleteClientAppointment: (id, aid) => api.delete(`/clients/${id}/appointments/${aid}`),
    addClientTask: (id, b) => api.post(`/clients/${id}/tasks`, b),
    updateClientTask: (id, tid, b) => api.put(`/clients/${id}/tasks/${tid}`, b),
    deleteClientTask: (id, tid) => api.delete(`/clients/${id}/tasks/${tid}`),
    deleteClient: (id) => api.delete(`/clients/${id}`),
    clientOwners: () => api.get("/clients/meta/owners"),
    checkClientDuplicates: (params) => api.get("/clients/duplicates/check", { params }),
    clientDuplicates: () => api.get("/clients/duplicates"),
    mergeClient: (id, sourceId) => api.post(`/clients/${id}/merge`, { source_id: sourceId }),
    crmOverview: () => api.get("/crm/overview"),
    crmAlerts: () => api.get("/crm/alerts"),
    crmSettings: () => api.get("/crm/settings"),
    saveCrmSettings: (b) => api.put("/crm/settings", b),
    createDebrief: (clientId, b) => api.post(`/crm/accounts/${clientId}/debrief`, b, { timeout: 120000 }),
    commitDebrief: (clientId, debriefId, b) => api.post(`/crm/accounts/${clientId}/debrief/${debriefId}/commit`, b),
    debriefs: (clientId) => api.get(`/crm/accounts/${clientId}/debriefs`),
    scanCard: (b) => api.post("/crm/scan-card", b, { timeout: 90000 }),
    proposeVoiceUpdate: (clientId, b) => api.post(`/crm/accounts/${clientId}/voice-update`, b, { timeout: 90000 }),
    applyVoiceUpdate: (clientId, b) => api.post(`/crm/accounts/${clientId}/voice-update/apply`, b),
    addClientNote: (id, b) => api.post(`/clients/${id}/notes`, b),
    deleteClientNote: (id, nid) => api.delete(`/clients/${id}/notes/${nid}`),

    contracts: (clientId) => api.get("/contracts", { params: clientId ? { client_id: clientId } : {} }),
    createContract: (b) => api.post("/contracts", b),
    updateContract: (id, b) => api.patch(`/contracts/${id}`, b),
    deleteContract: (id) => api.delete(`/contracts/${id}`),

    jobs: () => api.get("/jobs"),
    saveJob: (b) => api.post("/jobs", b),
    deleteJob: (id) => api.delete(`/jobs/${id}`),
    bulkDeleteJobs: (ids) => api.post("/jobs/bulk-delete", { ids }),
    importRom: (formData) =>
        api.post("/imports/rom", formData, { timeout: 60000 }),
    importInvoice: (formData) =>
        api.post("/imports/invoice", formData, { timeout: 150000 }),
    importRomBatch: (formData) =>
        api.post("/imports/rom/batch", formData, { timeout: 120000 }),
    importInvoiceBatch: (formData) =>
        api.post("/imports/invoice/batch", formData, { timeout: 240000 }),
    startInvoiceImport: (formData) =>
        api.post("/imports/invoice/start", formData, { timeout: 90000 }),
    invoiceImportStatus: (taskId) =>
        api.get(`/imports/invoice/status/${taskId}`, { timeout: 20000 }),

    docs: () => api.get("/documents"),
    saveDoc: (b) => api.post("/documents", b),
    signDoc: (id, b) => api.post(`/documents/${id}/sign`, b),
    proposalAlerts: () => api.get("/documents/proposal-alerts"),
    changeRequests: () => api.get("/documents/change-requests"),
    markProposalAlertsSeen: () => api.post("/documents/proposal-alerts/seen"),

    recordActual: (id, b) => api.post(`/jobs/${id}/actual`, b),
    jobVariance: (id) => api.get(`/jobs/${id}/variance`),
    jobVarianceAI: (id) => api.post(`/jobs/${id}/variance/ai`, {}, { timeout: 90000 }),
    calibrationAnalysis: () => api.get("/calibration/analysis"),
    calibrationBands: () => api.get("/calibration/bands"),
    cleaningAccuracy: () => api.get("/calibration/cleaning"),
    repositoryInsights: () => api.get("/insights/repository"),
    mlStatus: () => api.get("/calibration/ml/status"),
    applyTuning: (b) => api.post("/calibration/apply", b),
    setUserGold: (id, b) => api.patch(`/auth/users/${id}`, b),
    updateUser: (id, b) => api.patch(`/auth/users/${id}`, b),

    pushSync: (b) => api.post("/sync/push", b),
    pullSync: () => api.get("/sync/pull"),

    dashboard: () => api.get("/dashboard"),
    businessOverview: () => api.get("/dashboard/business"),
    businessBriefing: () => api.post("/dashboard/ai-briefing", {}, { timeout: 90000 }),

    // Timesheets
    employees: (params) => api.get("/timesheets/employees", { params }),
    saveEmployee: (b) => api.post("/timesheets/employees", b),
    updateEmployee: (id, b) => api.put(`/timesheets/employees/${id}`, b),
    deleteEmployee: (id) => api.delete(`/timesheets/employees/${id}`),
    bulkDeleteEmployees: (ids) => api.post("/timesheets/employees/bulk-delete", { ids }),
    timeEntries: (params) => api.get("/timesheets/entries", { params }),
    timeEntriesSummary: () => api.get("/timesheets/entries/summary"),
    timePendingCount: () => api.get("/timesheets/entries/pending-count"),
    createTimeEntry: (b) => api.post("/timesheets/entries/manual", b),
    updateTimeEntry: (id, b) => api.put(`/timesheets/entries/${id}`, b),
    approveTimeEntry: (id, b) => api.post(`/timesheets/entries/${id}/approve`, b || {}),
    rejectTimeEntry: (id, b) => api.post(`/timesheets/entries/${id}/reject`, b || {}),
    bulkApproveTime: (b) => api.post("/timesheets/entries/bulk-approve", b),
    bulkRejectTime: (b) => api.post("/timesheets/entries/bulk-reject", b),
    bulkDeleteTime: (ids) => api.post("/timesheets/entries/bulk-delete", { ids }),
    jobLabour: (jobId) => api.get(`/timesheets/job-labour/${jobId}`),

    // Projects
    projects: (params) => api.get("/projects", { params }),
    projectMeta: () => api.get("/projects/meta"),
    project: (id) => api.get(`/projects/${id}`),
    createProject: (b) => api.post("/projects", b),
    projectFromRom: (jobId, b) => api.post(`/projects/from-rom/${jobId}`, b || {}),
    updateProject: (id, b) => api.put(`/projects/${id}`, b),
    deleteProject: (id) => api.delete(`/projects/${id}`),
    projectLabour: (id) => api.get(`/projects/${id}/labour`),
    projectAiSummary: (id) => api.post(`/projects/${id}/ai-summary`, {}, { timeout: 90000 }),
    portfolio: () => api.get("/projects/portfolio"),

    // Schedules
    schedules: (params) => api.get("/schedules", { params }),
    createSchedule: (b) => api.post("/schedules", b),
    updateSchedule: (id, b) => api.put(`/schedules/${id}`, b),
    deleteSchedule: (id) => api.delete(`/schedules/${id}`),

    // Invoices & Payments
    invoices: (projectId) => api.get("/invoices", { params: projectId ? { project_id: projectId } : {} }),
    invoice: (id) => api.get(`/invoices/${id}`),
    createInvoiceFromProject: (projectId, b) => api.post(`/invoices/from-project/${projectId}`, b),
    updateInvoice: (id, b) => api.put(`/invoices/${id}`, b),
    voidInvoice: (id) => api.delete(`/invoices/${id}`),
    sendInvoice: (id, b) => api.post(`/invoices/${id}/send`, b || {}),
    recordPayment: (id, b) => api.post(`/invoices/${id}/payments`, b),
    deletePayment: (invoiceId, paymentId) => api.delete(`/invoices/${invoiceId}/payments/${paymentId}`),
    publicPayMock: (token) => api.post(`/public/documents/${token}/pay-mock`),

    // Files / object storage
    uploadFile: (formData) => api.post("/files/upload", formData, { headers: { "Content-Type": "multipart/form-data" } }),
    transcribeVoice: (formData) => api.post("/voice/transcribe", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 50000 }),
    fileDownloadUrl: (id) => `${api.defaults.baseURL}/files/${id}/download`,
    deleteFile: (id) => api.delete(`/files/${id}`),

    franchiseSettings: () => api.get("/franchise/settings"),
    saveFranchiseSettings: (b) => api.put("/franchise/settings", b),

    // AI Estimator
    aiEstimateChat: (b) => api.post("/ai-estimate/chat", b, { timeout: 60000 }),
    aiEstimateSession: (sessionId) => api.get(`/ai-estimate/session/${sessionId}`),
    aiProposal: (b) => api.post("/ai-estimate/proposal", b, { timeout: 120000 }),

    // Clock (Item 7 — QR site register)
    resolveClockCode: (code) => api.get(`/clock/resolve/${encodeURIComponent(code)}`),
    subContractorCompanies: (search) => api.get("/clock/companies", { params: search ? { search } : {} }),
    createSubContractorCompany: (b) => api.post("/clock/companies", b),
    deleteSubContractorCompany: (id) => api.delete(`/clock/companies/${id}`),
    findSubcontractorByEmail: (email) => api.get("/clock/subcontractors", { params: { email } }),
    upsertSubcontractor: (b) => api.post("/clock/subcontractors", b),
    subcontractorRates: () => api.get("/clock/rates"),
    saveSubcontractorRate: (b) => api.post("/clock/rates", b),
    deleteSubcontractorRate: (id) => api.delete(`/clock/rates/${id}`),
    clockActive: (projectId) => api.get("/clock/active", { params: projectId ? { project_id: projectId } : {} }),
    clockEvents: (projectId) => api.get("/clock/events", { params: projectId ? { project_id: projectId } : {} }),
    clockIn: (b) => api.post("/clock/in", b),
    clockOut: (eventId, b) => api.post(`/clock/out/${eventId}`, b || {}),
    approveClockEvent: (eventId, b) => api.post(`/clock/events/${eventId}/approve`, b),
    publicEmployees: () => api.get("/clock/public/employees"),
    publicProjects: () => api.get("/clock/public/projects"),
    employeeQrUrl: (id) => `${api.defaults.baseURL}/timesheets/employees/${id}/qr?base=${encodeURIComponent(window.location.origin)}`,
    regenerateEmployeeToken: (id) => api.post(`/timesheets/employees/${id}/regenerate-token`),
    projectQrUrl: (id) => `${api.defaults.baseURL}/projects/${id}/qr?base=${encodeURIComponent(window.location.origin)}`,
    regenerateSiteToken: (id) => api.post(`/projects/${id}/regenerate-site-token`),

    // Field logs (Item 8 — daily field documentation)
    fieldLogs: (params) => api.get("/field-logs", { params }),
    fieldLogRollup: (projectId) => api.get(`/field-logs/rollup/${projectId}`),
    createFieldLog: (b) => api.post("/field-logs", b),
    reviewFieldLog: (id, b) => api.post(`/field-logs/${id}/review`, b),
    deleteFieldLog: (id) => api.delete(`/field-logs/${id}`),

    // Moisture readings (Item 10 — daily humidity & psychrometric tracking)
    moistureReadings: (projectId) => api.get("/moisture-readings", { params: { project_id: projectId } }),
    moistureTrend: (projectId) => api.get(`/moisture-readings/trend/${projectId}`),
    createMoistureReading: (b) => api.post("/moisture-readings", b),
    deleteMoistureReading: (id) => api.delete(`/moisture-readings/${id}`),
    fieldRoomsTree: (jobId) => api.get("/field-events/rooms-tree", { params: { job_id: jobId } }),
    fieldMediaUrl: (id) => `${api.defaults.baseURL}/field-events/media/${id}/file`,
    checklistTemplates: () => api.get("/field/checklist-templates"),
    createChecklistTemplate: (b) => api.post("/field/checklist-templates", b),
    updateChecklistTemplate: (id, b) => api.put(`/field/checklist-templates/${id}`, b),
    deleteChecklistTemplate: (id) => api.delete(`/field/checklist-templates/${id}`),
    generateFieldScope: (jobId) => api.post(`/ai-estimate/scope/${jobId}`),
    getFieldScope: (jobId) => api.get(`/ai-estimate/scope/${jobId}`),
};
